import PropTypes from "prop-types"
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { apiBuscarAreasArmazenamento } from "../../helpers/backend_helper"
import ListDataTable from "../../components/Common/list-data-table"
import { describeDateTime, removeHour } from "../../helpers/utils"
import "react-datepicker/dist/react-datepicker.css"
import ptBR from "date-fns/locale/pt-BR"
import { ptBR as ptBRDatePickers } from "@mui/x-date-pickers"
import { registerLocale, setDefaultLocale } from "react-datepicker"
import { Box, Chip, TextField } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { Link } from "react-router-dom"
import { AvForm } from "availity-reactstrap-validation"
import { t } from "i18next"
import { getTipoAreaOptions, tipoAreaColumnOptions } from "./const"

const ptBRLocale =
  ptBRDatePickers.components.MuiLocalizationProvider.defaultProps.localeText

registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const AreasArmazenamentoTable = forwardRef((props, refFather) => {
  const ref = useRef()

  const {
    filters,
    columns,
    usePermissionRemove,
    usePermissionEdit,
    onEdit,
    onDuplicate,
    onRemove,
  } = props
  const [data, setData] = useState([])
  const [modalRemove, setModalRemove] = useState(false)
  const [itemSelecionado, setItemSelecionado] = useState()

  useImperativeHandle(refFather, () => ({
    search() {
      ref?.current?.search()
    },
  }))

  const showConfirmDelete = item => {
    setItemSelecionado(item)
    setModalRemove(true)
  }

  const remover = () => {
    if (onRemove) onRemove(itemSelecionado)
    setModalRemove(false)
  }

  const disponivelBadge = value =>
    value === "S" ? (
      <Chip label={t("areas.armazenamento.status.disponivel")} color="success" size="small" />
    ) : (
      <Chip label={t("areas.armazenamento.filters.inativas")} color="error" size="small" />
    )

  const sincronizarBadge = value =>
    value === "S" ? (
      <Chip
        label={t("areas.armazenamento.status.pendente")}
        color="warning"
        size="small"
        icon={<span>🟥</span>}
      />
    ) : (
      <Chip
        label={t("areas.armazenamento.status.sincronizado")}
        color="success"
        size="small"
        icon={<span>🟩</span>}
      />
    )

  const colunas = useMemo(
    () =>
      columns || [
        {
          header: t("areas.armazenamento.columns.idCloud"),
          accessorKey: "id",
          size: 80,
          minSize: 60,
          maxSize: 100,
          enableHiding: false,
          muiTableBodyCellProps: { align: "right" },
        },
        {
          header: t("areas.armazenamento.columns.razaoSocial"),
          accessorKey: "empresa_RazaoSocial",
          size: 220,
          filterFn: "contains",
        },
        { header: t("areas.armazenamento.columns.codigo"), accessorKey: "idId", size: 60 },
        {
          header: t("areas.armazenamento.columns.nomeArea"),
          accessorKey: "nome_area",
          size: 240,
          filterFn: "contains",
        },
        {
          header: t("areas.armazenamento.columns.tipoArea"),
          accessorKey: "tipo_area",
          size: 120,
          Cell: ({ cell }) => {
            const raw = cell?.getValue?.()
            const key = String(raw ?? '')
              .trim()
              .toUpperCase()
            const label = tipoAreaColumnOptions[key]
            return label || String(raw ?? '-')
          },
          filterSelectOptions: (Array.isArray(getTipoAreaOptions())
            ? getTipoAreaOptions().filter(opt => (opt?.value ?? "") !== "")
            : []
          ).map(opt => ({ text: String(opt.label ?? opt.value ?? ''), value: String(opt.value ?? '') })),
          filterVariant: 'multi-select',
        },
        {
          header: t("areas.armazenamento.columns.disponivelUso"),
          accessorKey: "disponivel",
          size: 160,
          enableColumnFilter: false,
          Cell: ({ cell }) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {disponivelBadge(cell.getValue())}
            </Box>
          ),
        },
        {
          header: t("areas.armazenamento.columns.pendenteSincronizacao"),
          accessorKey: "sincronizar",
          size: 200,
          enableColumnFilter: false,
          Cell: ({ cell }) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {sincronizarBadge(cell.getValue())}
            </Box>
          ),
        },
        {
          header: t("areas.armazenamento.columns.dataSincronizacao"),
          size: 180,
          Cell: ({ cell }) => describeDateTime(cell?.getValue()),
          accessorFn: row =>
            row?.data_sincronizacao ? new Date(row.data_sincronizacao) : null,
          accessorKey: "data_sincronizacao",
          sortingFn: "datetime",
          Filter: ({ column }) => (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
              localeText={ptBRLocale}
            >
              <DatePicker
                onChange={newValue => {
                  if (newValue) newValue = removeHour(newValue, false)
                  column.setFilterValue(newValue)
                }}
                inputFormat="dd/MM/yyyy"
                componentsProps={{
                  actionBar: { actions: ["accept", "clear"] },
                }}
                value={
                  column.getFilterValue()
                    ? new Date(column.getFilterValue())
                    : null
                }
                views={["year", "month", "day"]}
                renderInput={props => (
                  <TextField {...props} variant="standard" />
                )}
              />
            </LocalizationProvider>
          ),
        },
        {
          header: t("areas.armazenamento.columns.criadoPor"),
          accessorKey: "criado_por_nome",
          size: 160,
          enableColumnFilter: false,
        },
        {
          header: t("areas.armazenamento.columns.dataCriacao"),
          size: 180,
          Cell: ({ cell }) => describeDateTime(cell?.getValue()),
          accessorFn: row =>
            row?.data_criacao ? new Date(row.data_criacao) : null,
          accessorKey: "data_criacao",
          sortingFn: "datetime",
          Filter: ({ column }) => (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
              localeText={ptBRLocale}
            >
              <DatePicker
                onChange={newValue => {
                  if (newValue) newValue = removeHour(newValue, false)
                  column.setFilterValue(newValue)
                }}
                inputFormat="dd/MM/yyyy"
                componentsProps={{
                  actionBar: { actions: ["accept", "clear"] },
                }}
                value={
                  column.getFilterValue()
                    ? new Date(column.getFilterValue())
                    : null
                }
                views={["year", "month", "day"]}
                renderInput={props => (
                  <TextField {...props} variant="standard" />
                )}
              />
            </LocalizationProvider>
          ),
        },
        {
          header: t("areas.armazenamento.columns.alteradoPor"),
          accessorKey: "alterado_por_nome",
          size: 160,
          enableColumnFilter: false,
        },
        {
          header: t("areas.armazenamento.columns.dataAlteracao"),
          size: 200,
          Cell: ({ cell }) => describeDateTime(cell?.getValue()),
          accessorFn: row =>
            row?.data_alteracao ? new Date(row.data_alteracao) : null,
          accessorKey: "data_alteracao",
          sortingFn: "datetime",
          Filter: ({ column }) => (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
              localeText={ptBRLocale}
            >
              <DatePicker
                onChange={newValue => {
                  if (newValue) newValue = removeHour(newValue, false)
                  column.setFilterValue(newValue)
                }}
                inputFormat="dd/MM/yyyy"
                componentsProps={{
                  actionBar: { actions: ["accept", "clear"] },
                }}
                value={
                  column.getFilterValue()
                    ? new Date(column.getFilterValue())
                    : null
                }
                views={["year", "month", "day"]}
                renderInput={props => (
                  <TextField {...props} variant="standard" />
                )}
              />
            </LocalizationProvider>
          ),
        },
        {
          header: t("areas.armazenamento.columns.uuid"),
          accessorKey: "uuid",
          size: 280,
          enableColumnFilter: false,
        },
      ],
    []
  )

  return (
    <React.Fragment>
      <ListDataTable
        ref={ref}
        searchFn={async params => {
          try {
            const res = await apiBuscarAreasArmazenamento(params)
            if (Array.isArray(res?.data)) {
              return { data: res.data, count: res.count ?? res.total ?? 0 }
            }
            if (res?.success) {
              const payload = res.data ?? res.result ?? {}
              const rows = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload?.rows)
                ? payload.rows
                : Array.isArray(payload)
                ? payload
                : []
              const count =
                payload?.count ?? res.count ?? res.total ?? rows.length
              return { data: rows, count }
            }

            if (res?.rows || res?.count) {
              return { data: res.rows ?? [], count: res.count ?? 0 }
            }
            return { data: [], count: 0 }
          } catch (e) {
            console.error("[areas-armazenamento-table] searchFn error", e)
            return { data: [], count: 0 }
          }
        }}
        advancedFilters={filters}
        columns={colunas}
        columnVisibility={{
          sincronizar: false,
          data_sincronizacao: false,
          criado_por_nome: false,
          data_criacao: false,
          alterado_por_nome: false,
          data_alteracao: false,
          uuid: false,
          id_cloud: false,
        }}
        data={data}
        setData={setData}
        defaultSorting={[{ id: "id", desc: true }]}
        enableRowActions={true}
        renderRowActions={(row, table) => (
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Link to="#" className="text-primary">
              <i
                className="mdi mdi-eye font-size-18 ms-1"
                onClick={e => onEdit && onEdit(row.original)}
                title="Visualizar"
              />
            </Link>
            {usePermissionEdit && (
              <Link to="#" className="text-primary">
                <i
                  className="mdi mdi-pencil font-size-18 ms-1"
                  onClick={e => onEdit && onEdit(row.original)}
                  title="Editar"
                />
              </Link>
            )}
            {onDuplicate && usePermissionEdit && (
              <Link to="#" className="text-primary">
                <i
                  className="mdi mdi-content-copy font-size-18 ms-1"
                  onClick={e => onDuplicate(row.original)}
                  title="Duplicar"
                />
              </Link>
            )}
            {usePermissionRemove && (
              <Link to="#" className="text-danger">
                <i
                  className="mdi mdi-delete font-size-18"
                  onClick={() => showConfirmDelete(row.original)}
                  title="Remover"
                />
              </Link>
            )}
          </Box>
        )}
      />
      <Modal isOpen={modalRemove}>
        <ModalHeader tag="h4">Confirmação</ModalHeader>
        <ModalBody>
          <AvForm onValidSubmit={(e, v) => remover()}>
            <Row form>
              <Col className="col-12">
                Deseja realmente remover esse registro?
              </Col>
            </Row>
            <Row style={{ paddingTop: 10 }}>
              <Col>
                <div className="text-right">
                  <button type="submit" className="btn btn-success save-event">
                    Confirmar
                  </button>
                  <button
                    type="button"
                    className="btn btn-light ms-2"
                    onClick={e => setModalRemove(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </Col>
            </Row>
          </AvForm>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
})

AreasArmazenamentoTable.propTypes = {
  filters: PropTypes.array,
  columns: PropTypes.array,
  usePermissionRemove: PropTypes.bool,
  usePermissionEdit: PropTypes.bool,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onRemove: PropTypes.func,
}

export default AreasArmazenamentoTable
